import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, MapPin, ArrowUpRight } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useT } from "@/context/ContentContext";
import useHasBlog from "@/hooks/useHasBlog";
import FooterNewsletter from "@/components/FooterNewsletter";

const AREAS = [
    { to: "/remodelacoes-lisboa", label: "Lisboa" },
    { to: "/remodelacoes-cascais", label: "Cascais" },
    { to: "/remodelacoes-oeiras", label: "Oeiras" },
    { to: "/remodelacoes-sintra", label: "Sintra" },
    { to: "/remodelacoes-setubal", label: "Distrito de Setúbal" },
    { to: "/remodelacoes-santarem", label: "Distrito de Santarém" },
];

export default function Footer() {
    const { settings, whatsappLink } = useSettings();
    const t = useT();
    const hasBlog = useHasBlog();
    return (
        <footer className="bg-[hsl(var(--forest-deep))] text-white rounded-t-[2.5rem] mt-12">
            <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 lg:py-20">
                {/* Newsletter strip */}
                <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8 mb-12 grid md:grid-cols-[1.2fr_1fr] gap-6 items-center" data-testid="footer-newsletter">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest font-mono text-[hsl(var(--copper))] mb-2">Newsletter</div>
                        <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight">
                            Receba conteúdos sobre obras e <span className="font-serif-italic text-[hsl(var(--copper))]">5% de desconto</span> na sua primeira obra.
                        </h3>
                        <p className="text-sm text-white/60 mt-2">Sem spam. Só o que ajuda quem está a pensar remodelar.</p>
                    </div>
                    <FooterNewsletter />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
                    <div className="lg:col-span-1">
                        <Link to="/" className="flex items-center gap-3 mb-4">
                            <span className="logo-mark text-4xl text-white">H<span className="dot text-[hsl(var(--copper))]">.</span></span>
                            <span className="font-display font-bold text-lg">O Homem da Obra</span>
                        </Link>
                        <p className="text-sm text-white/70 leading-relaxed" data-testid="footer-tagline">
                            {t("footer.tagline", "Remodelação, gestão de obra e valorização de imóveis com acompanhamento próximo e orçamentos claros.")}
                        </p>
                    </div>

                    <div>
                        <h4 className="font-display font-semibold mb-4 text-sm">Navegação</h4>
                        <ul className="space-y-2 text-sm text-white/70">
                            <li><Link to="/servicos" className="hover:text-white transition">Serviços</Link></li>
                            <li><Link to="/trabalhos" className="hover:text-white transition">Trabalhos</Link></li>
                            <li><Link to="/transformacoes" className="hover:text-white transition">Transformações</Link></li>
                            <li><Link to="/sobre-nos" className="hover:text-white transition">Sobre Nós</Link></li>
                            {hasBlog && <li><Link to="/blog" className="hover:text-white transition">Blog</Link></li>}
                            <li><Link to="/orcamento" className="hover:text-white transition">Pedir orçamento</Link></li>
                            <li><Link to="/contactos" className="hover:text-white transition">Contactos</Link></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-display font-semibold mb-4 text-sm">Área de atuação</h4>
                        <ul className="space-y-2 text-sm text-white/70">
                            {AREAS.map((a) => (
                                <li key={a.to}>
                                    <Link to={a.to} className="flex items-center gap-2 hover:text-white">
                                        <MapPin className="w-3.5 h-3.5 text-[hsl(var(--copper))] flex-shrink-0" /> {a.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-display font-semibold mb-4 text-sm">Contactos</h4>
                        <ul className="space-y-3 text-sm text-white/80">
                            <li><a href={`tel:${settings.phone.replace(/\s/g,'')}`} className="flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4 text-[hsl(var(--copper))]" /> {settings.phone}</a></li>
                            <li><a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white"><MessageCircle className="w-4 h-4 text-[hsl(var(--copper))]" /> WhatsApp</a></li>
                            <li><a href={`mailto:${settings.email}`} className="flex items-center gap-2 hover:text-white break-all"><Mail className="w-4 h-4 text-[hsl(var(--copper))]" /> {settings.email}</a></li>
                        </ul>
                        <div className="mt-6">
                            <Link to="/orcamento" className="btn-copper !py-2 !px-4 text-sm">
                                Pedir orçamento
                                <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="pt-8 border-t border-white/15 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-xs text-white/60">
                    <p>© {new Date().getFullYear()} O Homem da Obra. Todos os direitos reservados.</p>
                    <div className="flex flex-wrap gap-4">
                        <Link to="/privacidade" className="hover:text-white">Política de Privacidade</Link>
                        <Link to="/cookies" className="hover:text-white">Política de Cookies</Link>
                        <Link to="/admin/login" className="hover:text-white" data-testid="footer-team-link">Área da equipa</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
